import React, { useState } from 'react';
import {
  DollarSign,
  ArrowLeft,
  Search,
  Filter,
  Plus,
  Edit,
  Eye,
  AlertTriangle,
  CheckCircle,
  TrendingUp,
  TrendingDown,
  Calendar,
  Clock,
  BarChart3,
  PieChart,
  Activity,
  Target,
  CreditCard,
  FileText,
  User,
  Building,
  Download,
  Upload,
  Settings
} from 'lucide-react';
import { budgetData } from '../../data/purchaseCycleData';

const BudgetControl = ({ onBack, navigationSource }) => {
  const [activeView, setActiveView] = useState('dashboard');
  const [selectedAllocation, setSelectedAllocation] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  const { budgetAllocations, budgetDashboard } = budgetData;

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-green-600 bg-green-100';
      case 'expired': return 'text-red-600 bg-red-100';
      case 'pending': return 'text-yellow-600 bg-yellow-100';
      case 'draft': return 'text-gray-600 bg-gray-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'active': return '有效';
      case 'expired': return '已過期';
      case 'pending': return '待核准';
      case 'draft': return '草稿';
      default: return '未知';
    }
  };

  const getDepartmentText = (department) => {
    switch (department) {
      case 'production': return '生產部門';
      case 'maintenance': return '維護部門';
      case 'procurement': return '採購部門';
      case 'logistics': return '物流部門';
      default: return department;
    }
  };

  const getCategoryText = (category) => {
    switch (category) {
      case 'raw_materials': return '原物料';
      case 'components': return '零組件';
      case 'spare_parts': return '備品備料';
      case 'equipment': return '設備';
      case 'services': return '服務';
      default: return category;
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('zh-TW', {
      style: 'currency',
      currency: 'TWD',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('zh-TW');
  };

  const getUtilizationColor = (rate) => {
    if (rate >= 90) return 'text-red-600';
    if (rate >= 70) return 'text-orange-600';
    if (rate >= 50) return 'text-yellow-600';
    return 'text-green-600';
  };

  const renderWorkflowBanner = () => {
    if (!navigationSource) return null;
    
    return (
      <div className="mb-6 bg-gradient-to-r from-orange-500 to-orange-600 text-white p-4 rounded-lg shadow-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <DollarSign className="w-6 h-6" />
            <div>
              <h2 className="text-lg font-semibold">採購週期管理 - 預算控制</h2>
              <p className="text-orange-100 text-sm">Purchase Cycle Management - Budget Control</p>
            </div>
          </div>
          <button
            onClick={onBack}
            className="flex items-center space-x-2 bg-white bg-opacity-20 hover:bg-opacity-30 px-4 py-2 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>返回採購週期</span>
          </button>
        </div>
      </div>
    );
  };

  const renderDashboard = () => (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-lg shadow-md">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">總預算</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(budgetDashboard.totalAllocated)}</p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-blue-600" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm">
            <TrendingUp className="w-4 h-4 text-green-500 mr-1" />
            <span className="text-green-600">已分配 {budgetDashboard.activeAllocations} 項</span>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-md">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">已支出</p>
              <p className="text-2xl font-bold text-orange-600">{formatCurrency(budgetDashboard.totalSpent)}</p>
            </div>
            <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
              <CreditCard className="w-6 h-6 text-orange-600" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm">
            <BarChart3 className="w-4 h-4 text-orange-500 mr-1" />
            <span className="text-orange-600">使用率 {budgetDashboard.overallUtilization}%</span>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-md">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">剩餘預算</p>
              <p className="text-2xl font-bold text-green-600">{formatCurrency(budgetDashboard.totalRemaining)}</p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <Target className="w-6 h-6 text-green-600" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm">
            <CheckCircle className="w-4 h-4 text-green-500 mr-1" />
            <span className="text-green-600">可用額度 {((budgetDashboard.totalRemaining / budgetDashboard.totalAllocated) * 100).toFixed(1)}%</span>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-md">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">部門數量</p>
              <p className="text-2xl font-bold text-gray-900">{budgetDashboard.departmentCount}</p>
            </div>
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <Building className="w-6 h-6 text-purple-600" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm">
            <FileText className="w-4 h-4 text-purple-500 mr-1" />
            <span className="text-purple-600">分類 {budgetDashboard.categoryCount} 項</span>
          </div>
        </div>
      </div>

      {/* Budget Utilization Chart */}
      <div className="bg-white p-6 rounded-lg shadow-md">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">預算使用率</h3>
        <div className="space-y-4">
          {budgetAllocations.map((allocation) => (
            <div key={allocation.id} className="flex items-center space-x-4">
              <div className="flex-1">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-gray-900">
                    {getDepartmentText(allocation.department)} - {getCategoryText(allocation.category)}
                  </span>
                  <span className={`text-sm font-medium ${getUtilizationColor(allocation.utilizationRate)}`}>
                    {allocation.utilizationRate.toFixed(1)}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className={`h-2 rounded-full ${
                      allocation.utilizationRate >= 90 ? 'bg-red-500' :
                      allocation.utilizationRate >= 70 ? 'bg-orange-500' :
                      allocation.utilizationRate >= 50 ? 'bg-yellow-500' : 'bg-green-500'
                    }`}
                    style={{ width: `${allocation.utilizationRate}%` }}
                  ></div>
                </div>
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>已用 {formatCurrency(allocation.spentAmount)}</span>
                  <span>總額 {formatCurrency(allocation.allocatedAmount)}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Budget Alerts */}
      {budgetDashboard.budgetAlerts.length > 0 && (
        <div className="bg-white p-6 rounded-lg shadow-md">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">預算警示</h3>
          <div className="space-y-3">
            {budgetDashboard.budgetAlerts.map((alert, index) => (
              <div key={index} className="flex items-center space-x-3 p-3 bg-yellow-50 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-yellow-600" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">{alert.message}</p>
                  <p className="text-xs text-gray-500">使用率: {alert.utilizationRate}%</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Monthly Trend */}
      <div className="bg-white p-6 rounded-lg shadow-md">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">月度支出趨勢</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {budgetDashboard.monthlyTrend.map((trend, index) => (
            <div key={index} className="border rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-medium text-gray-900">{trend.month}</h4>
                <Calendar className="w-4 h-4 text-gray-500" />
              </div>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">支出金額</span>
                  <span className="text-sm font-medium text-gray-900">{formatCurrency(trend.spent)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">預算總額</span>
                  <span className="text-sm font-medium text-gray-900">{formatCurrency(trend.allocated)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">使用率</span>
                  <span className={`text-sm font-medium ${getUtilizationColor((trend.spent / trend.allocated) * 100)}`}>
                    {((trend.spent / trend.allocated) * 100).toFixed(1)}%
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white p-6 rounded-lg shadow-md">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">快速操作</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button
            onClick={() => setActiveView('allocations')}
            className="flex items-center space-x-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <Eye className="w-5 h-5 text-blue-600" />
            <div className="text-left">
              <p className="font-medium text-gray-900">查看預算分配</p>
              <p className="text-sm text-gray-500">管理部門預算分配</p>
            </div>
          </button>
          
          <button
            onClick={() => setActiveView('spending')}
            className="flex items-center space-x-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <BarChart3 className="w-5 h-5 text-green-600" />
            <div className="text-left">
              <p className="font-medium text-gray-900">支出分析</p>
              <p className="text-sm text-gray-500">分析支出趨勢和模式</p>
            </div>
          </button>
          
          <button
            onClick={() => setActiveView('reports')}
            className="flex items-center space-x-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <FileText className="w-5 h-5 text-purple-600" />
            <div className="text-left">
              <p className="font-medium text-gray-900">預算報表</p>
              <p className="text-sm text-gray-500">生成預算執行報表</p>
            </div>
          </button>
        </div>
      </div>
    </div>
  );

  const renderAllocations = () => (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">預算分配</h2>
          <p className="text-gray-600">管理部門和分類的預算分配</p>
        </div>
        <div className="flex items-center space-x-3">
          <button className="flex items-center space-x-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors">
            <Plus className="w-4 h-4" />
            <span>新增分配</span>
          </button>
          <button className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors">
            <Download className="w-4 h-4" />
            <span>匯出</span>
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow-md">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="搜尋部門或分類..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          
          <select
            value={departmentFilter}
            onChange={(e) => setDepartmentFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="all">所有部門</option>
            <option value="production">生產部門</option>
            <option value="maintenance">維護部門</option>
            <option value="procurement">採購部門</option>
            <option value="logistics">物流部門</option>
          </select>
          
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="all">所有狀態</option>
            <option value="active">有效</option>
            <option value="expired">已過期</option>
            <option value="pending">待核准</option>
          </select>
          
          <button className="flex items-center space-x-2 bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors">
            <Settings className="w-4 h-4" />
            <span>設定</span>
          </button>
        </div>
      </div>

      {/* Allocations Table */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">部門/分類</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">預算年月</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">分配金額</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">已支出</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">剩餘金額</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">使用率</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">狀態</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">核准人</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">操作</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {budgetAllocations.map((allocation) => (
                <tr key={allocation.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{getDepartmentText(allocation.department)}</div>
                      <div className="text-sm text-gray-500">{getCategoryText(allocation.category)}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {allocation.budgetYear}-{allocation.budgetMonth.toString().padStart(2, '0')}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatCurrency(allocation.allocatedAmount)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatCurrency(allocation.spentAmount)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatCurrency(allocation.remainingAmount)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
                        <div 
                          className={`h-2 rounded-full ${
                            allocation.utilizationRate >= 90 ? 'bg-red-500' :
                            allocation.utilizationRate >= 70 ? 'bg-orange-500' :
                            allocation.utilizationRate >= 50 ? 'bg-yellow-500' : 'bg-green-500'
                          }`}
                          style={{ width: `${allocation.utilizationRate}%` }}
                        ></div>
                      </div>
                      <span className={`text-sm font-medium ${getUtilizationColor(allocation.utilizationRate)}`}>
                        {allocation.utilizationRate.toFixed(1)}%
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(allocation.status)}`}>
                      {getStatusText(allocation.status)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {allocation.approverName}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => {
                          setSelectedAllocation(allocation);
                          setActiveView('allocation-detail');
                        }}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button className="text-green-600 hover:text-green-900">
                        <Edit className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderAllocationDetail = () => {
    if (!selectedAllocation) return null;

    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => setActiveView('allocations')}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                {getDepartmentText(selectedAllocation.department)} - {getCategoryText(selectedAllocation.category)}
              </h2>
              <p className="text-gray-600">{selectedAllocation.budgetYear}-{selectedAllocation.budgetMonth.toString().padStart(2, '0')}</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <button className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors">
              <Edit className="w-4 h-4" />
              <span>編輯</span>
            </button>
            <button className="flex items-center space-x-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors">
              <Download className="w-4 h-4" />
              <span>匯出</span>
            </button>
          </div>
        </div>

        {/* Allocation Overview */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">分配總額</h3>
            <p className="text-3xl font-bold text-blue-600">{formatCurrency(selectedAllocation.allocatedAmount)}</p>
            <p className="text-sm text-gray-500 mt-2">核准日期: {formatDate(selectedAllocation.approvalDate)}</p>
          </div>
          
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">已支出</h3>
            <p className="text-3xl font-bold text-orange-600">{formatCurrency(selectedAllocation.spentAmount)}</p>
            <p className="text-sm text-gray-500 mt-2">使用率: {selectedAllocation.utilizationRate.toFixed(1)}%</p>
          </div>
          
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">剩餘金額</h3>
            <p className="text-3xl font-bold text-green-600">{formatCurrency(selectedAllocation.remainingAmount)}</p>
            <p className="text-sm text-gray-500 mt-2">可用比例: {((selectedAllocation.remainingAmount / selectedAllocation.allocatedAmount) * 100).toFixed(1)}%</p>
          </div>
        </div>

        {/* Budget Details */}
        <div className="bg-white p-6 rounded-lg shadow-md">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">預算詳情</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-500">部門</p>
                <p className="font-medium text-gray-900">{getDepartmentText(selectedAllocation.department)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">分類</p>
                <p className="font-medium text-gray-900">{getCategoryText(selectedAllocation.category)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">預算年月</p>
                <p className="font-medium text-gray-900">{selectedAllocation.budgetYear}-{selectedAllocation.budgetMonth.toString().padStart(2, '0')}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">狀態</p>
                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(selectedAllocation.status)}`}>
                  {getStatusText(selectedAllocation.status)}
                </span>
              </div>
            </div>
            
            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-500">核准人</p>
                <p className="font-medium text-gray-900">{selectedAllocation.approverName}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">核准日期</p>
                <p className="font-medium text-gray-900">{formatDate(selectedAllocation.approvalDate)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">最後更新</p>
                <p className="font-medium text-gray-900">{formatDate(selectedAllocation.lastUpdated)}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Budget Utilization */}
        <div className="bg-white p-6 rounded-lg shadow-md">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">預算使用情況</h3>
          <div className="space-y-4">
            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-gray-700">預算使用進度</span>
                <span className="text-sm font-medium text-gray-900">{selectedAllocation.utilizationRate.toFixed(1)}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-4">
                <div 
                  className={`h-4 rounded-full ${
                    selectedAllocation.utilizationRate >= 90 ? 'bg-red-500' :
                    selectedAllocation.utilizationRate >= 70 ? 'bg-orange-500' :
                    selectedAllocation.utilizationRate >= 50 ? 'bg-yellow-500' : 'bg-green-500'
                  }`}
                  style={{ width: `${selectedAllocation.utilizationRate}%` }}
                ></div>
              </div>
            </div>
            
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <p className="text-sm text-gray-500">已分配</p>
                <p className="text-lg font-semibold text-blue-600">{formatCurrency(selectedAllocation.allocatedAmount)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">已使用</p>
                <p className="text-lg font-semibold text-orange-600">{formatCurrency(selectedAllocation.spentAmount)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">剩餘</p>
                <p className="text-lg font-semibold text-green-600">{formatCurrency(selectedAllocation.remainingAmount)}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderContent = () => {
    switch (activeView) {
      case 'dashboard':
        return renderDashboard();
      case 'allocations':
        return renderAllocations();
      case 'allocation-detail':
        return renderAllocationDetail();
      default:
        return renderDashboard();
    }
  };

  return (
    <div className="p-6">
      {renderWorkflowBanner()}
      
      {/* Navigation Tabs */}
      <div className="mb-6">
        <nav className="flex space-x-8">
          <button
            onClick={() => setActiveView('dashboard')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeView === 'dashboard'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            儀表板
          </button>
          <button
            onClick={() => setActiveView('allocations')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeView === 'allocations'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            預算分配
          </button>
        </nav>
      </div>

      {renderContent()}
    </div>
  );
};

export default BudgetControl; 